import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs/Observable';
import { of } from 'rxjs/observable/of';
import { MatMenuTrigger } from '@angular/material/menu';
import { Subject } from 'rxjs/Subject';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Store } from '@ngrx/store';
import { catchError, distinctUntilChanged, map, take } from 'rxjs/operators';
import * as _ from 'lodash';

import {
  IAdvancedSearchConfiguration,
  IAdvancedSearchQueryConfiguration,
  IDisplayConfig,
  AdvancedSearchType,
  AdvancedSearchResourceType,
  IDisplayColumn,
  IStateToGo,
  ITestNavigateParameters
} from './advanced-search-configuration.interface';
import * as selectors from '@ispm/dashboard/services/advanced-search.selector';
import { AdvancedSearchService } from '../../services/advanced-search.service';
import { CoreActionCreatorService } from '@ispm/core/services/core-action-creator.service';
import { DateHandler } from '@ispm/shared/utils/DateHandler';
import { DynamicSearchService } from '../../../worklist/services/dynamic-search.service';
import { IDiagnosticOrder } from '@ispm/test/interfaces/diagnostic-order.interface';
import { IUrlParams } from '@ispm/core/interfaces/router-handler.interface';
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import { NOTIFICATION_MESSAGE } from '@ispm-client/modules/aggregator/services/consts';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import { RouterManagerService } from '@ispm/core/services/router-manager.service';
import { SimpleSearchOrigin } from '../../../worklist/interfaces/simple-search.interface';
import { SimpleSearchService } from '../../../worklist/services/simple-search.service';
import { UserActionCreatorService } from '@ispm/user/services/user-action-creator.service';
import { WorkflowService } from '@ispm/workflow/services/workflow.service';
import { currentDiagnosticOrderSelector } from '@ispm/dashboard/test/store/diagnostic-order.selector';
import { PatientService } from '@ispm-client/modules/patient/services/patient.service';
import { IspmHttpClient } from '@ispm/core/services/ispm-http-client';

import {Router, ActivatedRoute} from '@angular/router';
import {UserService} from '@ispm/user/services/user.service';
import { WorklistHelper } from '../../../worklist/worklist-helper';
const QUERY_INPUT_PLACEHOLDER = '##';
const SEARCH_DEBOUNCE_DEFAULT = 2000;
const SEARCH_MIN_LENGTH = 2;
const BLUR_LOSS_DEBOUNCE = 50;

@Component({
  selector: 'gen-case-search',
  templateUrl: './case-search.component.html',
  styleUrls: ['./case-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CaseSearchComponent implements OnInit, OnDestroy {
  inputControl = new FormControl();
  searchResults$: Observable<any[]>;
  placeholderText: string = '';
  configurationRoot: IAdvancedSearchConfiguration;
  queryConfig: Array<IAdvancedSearchQueryConfiguration>;

  @ViewChild(MatMenuTrigger, { static: false}) menuTrigger: MatMenuTrigger;

  private focus$: Subject<boolean> = new BehaviorSubject(false);
  private destroyed$: Subject<any> = new Subject();
  private configuration$: Observable<IAdvancedSearchConfiguration>;
  private searchDebounce: number = SEARCH_DEBOUNCE_DEFAULT;

  constructor(
    private cd: ChangeDetectorRef,
    private coreAC: CoreActionCreatorService,
    private dynamicSearchService: DynamicSearchService,
    private notificationMessage: NOTIFICATION_MESSAGE,
    private notificationMessageService: NotificationMessageService,
    private routerManagerService: RouterManagerService,
    private simpleSearchService: SimpleSearchService,
    private store: Store<IgenomicsState>,
    private userAC: UserActionCreatorService,
    private workflowService: WorkflowService,
    public advancedSearchService: AdvancedSearchService,
    private patientService: PatientService,
    private _ispmHttpClient : IspmHttpClient,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private userService: UserService
  ) {
  }

  ngOnInit() {
    this.advancedSearchService.refetch();
    this.loadConfigurationFromStore();

    this.searchResults$ = this.configuration$
      .do(config => {
        this.configurationRoot = config;
        this.queryConfig = this.configurationRoot.queryConfig;
        this.placeholderText = this.configurationRoot.placeholder;
        this.searchDebounce = this.configurationRoot.searchDebounce ? this.configurationRoot.searchDebounce : SEARCH_DEBOUNCE_DEFAULT;
        this.cd.markForCheck();
      })
      .switchMapTo(this.inputControl.valueChanges)
      .debounceTime(this.searchDebounce)
      .switchMap(value => value.length > SEARCH_MIN_LENGTH ? this.search(value) : Observable.of([]))
      .publishReplay(1)
      .refCount();

    this.searchResults$.combineLatest(this.focus$.debounceTime(BLUR_LOSS_DEBOUNCE)) // Debouncing focus, otherwise can't double click to highlight text and it's annoying.
      .subscribe(([results, isFocused]) => {
        if ((results as []).length > 0 && isFocused && !this.menuTrigger.menuOpen) {
          this.menuTrigger.openMenu();
        } else if ((results as []).length === 0 && this.menuTrigger.menuOpen) {
          this.menuTrigger.closeMenu();
        }
      });
  }

  loadConfigurationFromStore() {
    this.configuration$ = this.store.select(selectors.advancedSearchConfigurationSelector)
      .filter(config => !_.isEmpty(config))
      .takeUntil(this.destroyed$.asObservable());
  }

  unifySimilarArrays(resultsArray): any[] {
    // Unify similar arrays and remove duplications.
    // 'results' is an array of arrays that can be of a few types (SearchType enum) e.g. patients and tests
    // and can have duplicate elements - for example, when searching for patient name and MRN and the search string can be in both
    if (!Array.isArray(resultsArray)) {
      return;
    }

    if (resultsArray.length <= 1) { // Gouping is not needed ifonnly one array exists
      return resultsArray;
    }
    let sortedArray: any[] = resultsArray.filter(arr => Array.isArray(arr) && arr.length > 0); // remove empty arrays
    // Grouping arrays by type (of the first element in of each array)
    for (let i = 1; i < sortedArray.length; i++) {
      for (let j = 0; j < i; j++) {
        if (sortedArray[i] && sortedArray[j] && sortedArray[j][0].resourceType === sortedArray[i][0].resourceType) {
          sortedArray[j] = sortedArray[i].concat(sortedArray[j]);
          sortedArray[i] = null;
        }
      }
    }
    sortedArray = _.compact(sortedArray); // remove null items
    for (let i = 0; i < sortedArray.length; i++) { // Remove duplicate elements
      sortedArray[i] = sortedArray[i].sort((a, b) => {
        if (a.test && a.test.meta && a.test.meta.created && b.test && b.test.meta && b.test.meta.created) {
          // Sorting patient tests according to creation time in descending order - this is needed when a certain patient has more than one test of the same type (e.g. 2 PNC's) and the newest should be opened
          return DateHandler.getTime(new Date(b.test.meta.created)) - DateHandler.getTime(new Date(a.test.meta.created));
        }
        if (a.test && a.test.meta && a.test.meta.created) { // if only one of the items has test, it should be the first
          return -1;
        }
        return 1;
      });
      sortedArray[i] =  _.uniqBy(sortedArray[i], 'id'); // uniqBy returns the first occurrence of each element. The array is sorted by test last update so the latest item will be returned.
    }
    return sortedArray;
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onFocus() {
    this.focus$.next(true);
  }

  onBlur() {
    this.focus$.next(false);
  }

  onRowSelected(selectedItem: any): void {
    switch (selectedItem.searchType) {
      case AdvancedSearchType.Patient:
        this.patientSelected(selectedItem);
        break;
      case AdvancedSearchType.Test:
        this.testSelected(selectedItem);
        break;
    }
    this.inputControl.reset('');
  }

  displayConfigToColumnDefs(searchResult) {
    let displayConfig: IDisplayConfig;
    if (searchResult && Array.isArray(searchResult) && searchResult.length > 0) {
      // Search for grid display according to the item search type (all items within the same array has the same type so looking for the searchType of the first item)
      displayConfig = this.configurationRoot.displayConfig.find(config => config.type === searchResult[0].searchType);
    }
    if (displayConfig) {
      return displayConfig.displayColumn.map(config => ({ title: config.title, mapFn: this.buildMapByDisplayConfig(config)}));
    }
    else {
      console.error('displayConfigToColumnDefs: no displayConfig was found.');
      return displayConfig;
    }
  }

  tableTitle(searchResult): string {
    let displayConfig: IDisplayConfig;
    let title: string;
    if (searchResult && Array.isArray(searchResult) && searchResult.length > 0) {
      displayConfig = this.configurationRoot.displayConfig.find(config => config.type === searchResult[0].searchType);
      if (displayConfig) {
        title = displayConfig.title;
      }
    }
    return title;
  }

  private buildMapByDisplayConfig = (config: IDisplayColumn) => data => {
    let ret = Array.isArray(config.paths)
      ? config.paths
        .map(path => _.get(data, path))
        .reduce((s, val) => `${s}${config.pathsSeperator}${val}`, '')
      : _.get(data, config.paths);

    if (config.prefix) {
      ret = config.prefix + ret;
    }
    if (config.suffix) {
      ret = ret + config.suffix;
    }
    return ret;
  }

  private search(value: string): Observable<Array<Array<any>>> {
    if (sessionStorage.getItem('worklistChanged')) {
      this.advancedSearchService.reloadWorklistParameters();
      this.loadConfigurationFromStore();
      sessionStorage.removeItem('worklistChanged');
    }
    const searchResults = this.queryConfig.map(conf => {
      const config = _.cloneDeep(conf); // TODO temporary solution. need to fetch config from store
      return Observable.from(Array.isArray(config) ? config : [config])
        .map(query => {
          if (!this.simpleSearchService.isSimpleSearch(query)) {
            const searchValue = value.split(' ').filter(v => v.trim());
            const searchValueFilter = query.dataQuery.filters.find(f => f.value === QUERY_INPUT_PLACEHOLDER);
            const newFilters = query.dataQuery.filters.filter(f => f.value !== QUERY_INPUT_PLACEHOLDER);

            searchValue.forEach(v => {
              searchValueFilter.value = v;
              newFilters.push({ ...searchValueFilter});
            });

            query.dataQuery.filters = newFilters;
          }
          return query;
        })
        .mergeMap((config) => {
          if (this.simpleSearchService.isSimpleSearch(config)) {
            return this.simpleSearchService.search(config.simpleSearchQuery, SimpleSearchOrigin.ADVANCED_SEARCH, value, 0,
              config.simpleSearchQuery.sort, -1).pipe(
              map(results => {
                if (this.simpleSearchService.requestError(results)) {
                  return [];
                }
                else {
                  return this.simpleSearchService.setAdvancedSearchResult(results, config);
                }
              }),
              catchError(err => {
                console.error('CaseSearchComponent->search: Simple search error', err);
                return Observable.of([]);
              })
            );
          }
          else {
            return this.dynamicSearchService.sendPureRequest(config.dataQuery, config.entityTreeQueryResource, 'relations').pipe(
              map(results => this.convertResultArray(results, config.resource, config.type)),
              catchError(err => {
                console.error('CaseSearchComponent->search: Dynamic search error', err);
                return Observable.of([]);
              })
            );
          }
        });
    });
    return Observable.zip(...searchResults)
      .map(results => {
        // results.every(result => result.length === 0) ? [] : results;
        results = this.unifySimilarArrays(results);
        return results;
      }).switchMap(results => {
        if(results==null || results.length<=0){
          return of([]);
        }

        //find array index which has patients data 
        let patientIndex = results.indexOf(results.find(a=>_.get(a,'[0].resourceType', '')?.toLowerCase() == 'patient'));
        if(patientIndex < 0){
          return of(results);
        }

        let patientResult = results[patientIndex];
        let patientLinks = this.patientService.getUniqueLinkPatientIdsStr(patientResult, 'id', 'otherKnownIds')
        if(patientLinks.length > 0) {
          let url = `entity-tree/patient?_id=${patientLinks}`
          return this._ispmHttpClient.get(url).pipe(
            map(linkedPatientList => {
              patientResult.push(linkedPatientList[0])
              let filteredPatientList = this.refinePatientSearchResults(patientResult)              
              results[patientIndex] = filteredPatientList;
              return results;
            }
          ))
        } else {
          results[patientIndex] = this.refinePatientSearchResults(results[patientIndex]);
          return of(results);
        }
      });      
  }

  // This funciton refines the patient search results by considering all the linked
  // patients and includes the only one (latest) patient object per linked group
  private refinePatientSearchResults(patientSearchResults){
    let filteredPatientList = this.patientService.groupPatientsByLink(patientSearchResults)
    return filteredPatientList
  }

  private convertResultArray(entries: any[], resourceType: AdvancedSearchResourceType, searchType: AdvancedSearchType) {
    let result: any[] = [];
    if (!entries || entries.length === 0) {
      return result;
    }

    if (searchType === AdvancedSearchType.Test) {
      result = _.flatten(entries);
      result = result.map(item => ({ ...item, searchType: searchType})); // Adding 'searchType' to the array elements. needed for grid columns presentation
    }
    else if (searchType === AdvancedSearchType.Patient) {
      result = entries.map(en => {
        const obj = en.find(el => el.resourceType === AdvancedSearchResourceType.Patient);
        if (obj) {
          obj.searchType = searchType;
          const test = en.find(el => el.resourceType === resourceType);
          obj.test = test ? test : { };
        }
        return obj;
      });
    }
    else {
      console.error('convertResultArray: type -> ' + searchType + ' was not found!');
    }
    return result;
  }

  private patientSelected(patient: any) {
    const stateToGo: IStateToGo = this.configurationRoot.stateToGo.find(element => element.type === AdvancedSearchType.Patient);
    if (stateToGo) {
      const routeObject: IUrlParams = this.routerManagerService.getUrlObjectFromConfig(patient, stateToGo.routes);
      if (!routeObject) {
        console.error('CaseSearchComponent->patientSelected: Cannot find routes configuration');
        this.notificationMessageService.error(this.notificationMessage.ERROR_WHILE_SEARCH_FOR_TEST_FROM_WORKLIST);
        return;
      }
      this.coreAC.setLoader(true);
      const testDetails: ITestNavigateParameters = this.getQueryParameters(routeObject.params, patient);

      // If test does not exist for the patient, setting emptyPage = true so it will navigate to the empty page.
      const emptyPage: boolean = !Boolean(testDetails.testId) && !Boolean(testDetails.revisionId);

      if (!emptyPage) {
        this.workflowService.getWorklfowById(testDetails.testId) // Getting the last revision of the test
          .then(async (workflowResult) => {
            const currOrder = await this.store.select(currentDiagnosticOrderSelector).pipe(distinctUntilChanged(), take(1)).toPromise();
            if (currOrder && currOrder.workflowRequest) { // Do not open the current test again
              if (currOrder.workflowRequest.activeRevision === workflowResult.body.activeRevision &&
                currOrder.workflowRequest.id === testDetails.testId) {
                console.log('Trying to open the current test again');
                this.coreAC.setLoader(false);
                return;
              }
            }
            let activeRevistion = workflowResult.body.activeRevision;
            // Checking if active revision is None if so we take last item on revistions
            if (activeRevistion.toLowerCase() === 'none') {
              activeRevistion = workflowResult.body.revisions[workflowResult.body.revisions.length - 1].id;
            }
            testDetails.revisionId = activeRevistion;
            const redirectUrl = this.routerManagerService.mapStateToUrl(routeObject.URL);
            const skipLocationChange = redirectUrl.indexOf(WorklistHelper.PATHOLOGIST_ROUTE) > -1;
            this.router.navigate([redirectUrl, testDetails.patientId,
              testDetails.testId, testDetails.revisionId], {queryParams: this.routerManagerService.queryParameters, queryParamsHandling: 'preserve',skipLocationChange: skipLocationChange});
          }).catch(() => {
          this.coreAC.setLoader(false);
          this.notificationMessageService.error(this.notificationMessage.ERROR_WHILE_SEARCH_FOR_TEST_FROM_WORKLIST);
        });
      }
      else {
        this.userService.getPatient(testDetails.patientId, true, true).pipe(take(1)).subscribe();
        let route: string = routeObject.URL;
        // In case a dedicated empty page is configured (e.g. in Oncology Pathways), getting its url and type parameter
        if (routeObject.routeOnEmpty) {
          route = routeObject.routeOnEmpty;
          if (routeObject.typeOnEmpty) {
            testDetails.type = routeObject.typeOnEmpty;
          }
        }
        this.router.navigate([this.routerManagerService.mapStateToUrl(route), testDetails.patientId], {queryParams: this.routerManagerService.queryParameters});
        this.coreAC.setLoader(false);
      }
    }
    else {
      console.error('CaseSearchComponent-> patientSelected: no configuration or no stateToGo was found for patient');
    }
  }

  private testSelected(diagnosticOrder: any) {
    const stateToGo: IStateToGo = this.configurationRoot.stateToGo.find(element => element.type === AdvancedSearchType.Test);
    if (stateToGo) {
      const routeObject: IUrlParams = this.routerManagerService.getUrlObjectFromConfig(diagnosticOrder, stateToGo.routes);
      if (routeObject) {
        this.coreAC.setLoader(true);
        const testDetails: ITestNavigateParameters = this.getQueryParameters(routeObject.params, diagnosticOrder);

        this.workflowService.getWorklfowById(testDetails.testId) // Getting the last revision of the test
          .then(async (workflowResult) => {
            const currOrder = await this.store.select(currentDiagnosticOrderSelector).pipe(distinctUntilChanged(), take(1)).toPromise();
            if (currOrder && currOrder.workflowRequest) { // Do not open the current test again
              if (currOrder.workflowRequest.activeRevision === workflowResult.body.activeRevision &&
                currOrder.workflowRequest.id === testDetails.testId) {
                console.log('Trying to open the current test again');
                this.coreAC.setLoader(false);
                return;
              }
            }
            // Checking if active revision is None if so we take last item on revistions
            let activeRevistion = workflowResult.body.activeRevision;
            if (activeRevistion.toLowerCase() === 'none') {
              activeRevistion = workflowResult.body.revisions[workflowResult.body.revisions.length - 1].id;
            }
            testDetails.revisionId = activeRevistion;
            //Create a parameters array for navigate()
            let routeParameters = Object.values(testDetails).reverse();
            routeParameters.unshift(this.routerManagerService.mapStateToUrl(routeObject.URL)) //URL should be the first parameter
            this.router.navigate(routeParameters, { queryParams: this.routerManagerService.queryParameters, queryParamsHandling: 'preserve' });            
          }).catch(() => {
          this.coreAC.setLoader(false);
          this.notificationMessageService.error(this.notificationMessage.ERROR_WHILE_SEARCH_FOR_TEST_FROM_WORKLIST);
        });
      }
      else {
        console.error('CaseSearchComponent-> testSelected: routeObject is empty');
        this.notificationMessageService.error(this.notificationMessage.ERROR_WHILE_SEARCH_FOR_TEST_FROM_WORKLIST);
      }
    }
    else {
      console.error('CaseSearchComponent-> testSelected: no configuration or no stateToGo was found for test');
    }
  }

  private async getWorkflowIdFromDiagnosticOrder(diagnosticOrder: IDiagnosticOrder): Promise<string> {
    if (diagnosticOrder.workflowRequest && typeof diagnosticOrder.workflowRequest === 'object') {
      return diagnosticOrder.workflowRequest.id;
    }

    let workflowRequestId = diagnosticOrder.workflowRequest as any;
    if (diagnosticOrder.ordererReference) {
      // support diagnostic order with multiple workflow request
      const user = await this.userAC.getCurrentUser().toPromise();
      if (user && diagnosticOrder.ordererReference.organization === user.organization) {
        workflowRequestId = diagnosticOrder.ordererReference.workflowId;
      } else {
        const workflowIdRegex = new RegExp(`(${diagnosticOrder.ordererReference.workflowId},)|(,${diagnosticOrder.ordererReference.workflowId})`, 'g');
        workflowRequestId = workflowRequestId.replace(workflowIdRegex, '');
      }
    }
    return workflowRequestId;
  }

  private getQueryParameters(stateToGoParams: { [key: string]: string}, testParams: any): ITestNavigateParameters {
    const output: ITestNavigateParameters = { revisionId: null, testId: null};
    if (stateToGoParams && testParams) {
      for (const [paramName, paramValue] of Object.entries(stateToGoParams)) {
        if (paramValue) {
          output[paramName] = _.get(testParams, paramValue.toString(), '');
        }
      }
    }
    return output;
  }
}
